import React, { useContext } from "react";
import { useQuery } from "@apollo/client/react/hooks/useQuery.js";

import {
    GET_STATIC_CONTENT,
    GetStaticContentData,
    GetStaticContentVariables,
} from "../../graphql/queries/static-content.js";
import ErrorMsg from "./errors/ErrorMsg.js";
import Spinner from "../Spinner.js";
import MetaTags from "../MetaTags.js";
import { Content } from "../layout/CommonContentContextProvider.js";
import MarkdownRenderer from "./MarkdownRenderer.js";
import { container } from "../styles/common.css.js";
import { staticText, staticTitle } from "./StaticTextContent.css.js";
import NotFoundErrorMsg from "./errors/NotFoundErrorMsg.js";

interface Props {
    id: string;
    path: string;
}

interface ContentStructure {
    title: string;
    metaDescription: string;
    _text: string;
}

const StaticTextContent: React.FC<Props> = ({ id, path }) => {
    const { langCode } = useContext(Content);
    const { data, loading, error } = useQuery<
        GetStaticContentData<ContentStructure>,
        GetStaticContentVariables
    >(GET_STATIC_CONTENT, {
        variables: {
            id,
            langCode,
        },
    });

    if (error) {
        return <ErrorMsg error={error} />;
    }

    if (loading) {
        return <Spinner />;
    }

    if (data && data.staticContent) {
        const {
            contentLangs,
            content: { _text, title, metaDescription },
        } = data.staticContent;
        return (
            <>
                <MetaTags
                    currentLang={langCode}
                    langs={contentLangs}
                    path={path}
                    title={title}
                    description={metaDescription}
                />

                <main className={container.flex750}>
                    <h1 className={staticTitle}>{title}</h1>
                    <MarkdownRenderer className={staticText} source={_text} />
                </main>
            </>
        );
    }

    return <NotFoundErrorMsg />;
};

export default StaticTextContent;
